import { useNavigate } from "react-router-dom";
import { WarningCircle, ArrowRight, ChartBar } from "@phosphor-icons/react";
import { useWallet } from "@/lib/useWallet";

/**
 * CreditLowBanner — sticky alert ribbon at the top of the dashboard that
 * appears automatically when wallet usage crosses 80% / 90% / 100%.
 *
 * Mount once at the top of any page that should warn the operator. Hides
 * itself when balance is healthy. Colour ramps: amber (warn80) → orange
 * (warn90) → red (critical). At 100% the CTA changes from "Ver pacotes"
 * to "Comprar agora" and the banner can't be dismissed.
 *
 * Behaviour decisions:
 *   - We pull from `useWallet()` so the banner re-renders automatically
 *     on the 30s polling tick and whenever a 402 fires.
 *   - The "Dismiss" button only hides the banner for the current session
 *     when below 100% — at 100% there's nothing to dismiss; the operation
 *     is fully paused anyway.
 */

const STYLES = {
  warn80: {
    bg: "bg-amber-50 border-amber-300/70",
    iconBg: "bg-amber-500/15 text-amber-700",
    title: "Você está usando bem seus créditos do mês",
    title_color: "text-amber-900",
    body_color: "text-amber-800",
    cta: "Ver pacotes",
  },
  warn90: {
    bg: "bg-orange-50 border-orange-300",
    iconBg: "bg-orange-500/15 text-orange-700",
    title: "Atenção — seus créditos estão quase esgotando",
    title_color: "text-orange-900",
    body_color: "text-orange-800",
    cta: "Comprar pacote agora",
  },
  critical: {
    bg: "bg-rose-50 border-rose-300",
    iconBg: "bg-rose-500/15 text-rose-700",
    title: "Operação pausada — créditos esgotados",
    title_color: "text-rose-900",
    body_color: "text-rose-800",
    cta: "Comprar agora pra reativar",
  },
};

export default function CreditLowBanner({ compact = false }) {
  const { wallet } = useWallet();
  const navigate = useNavigate();

  if (!wallet) return null;
  const t = wallet.threshold;
  if (t === "ok") return null;
  const s = STYLES[t];
  if (!s) return null;

  const percent = Math.min(100, Math.round(wallet.percent_used || 0));
  const used = wallet.period_used || 0;
  const grant = wallet.monthly_grant || 0;

  return (
    <div
      data-testid={`credit-banner-${t}`}
      className={`relative rounded-lg border ${s.bg} ${compact ? "p-3" : "p-4"} flex items-start gap-3`}
    >
      <div className={`w-9 h-9 rounded-md ${s.iconBg} flex items-center justify-center flex-shrink-0`}>
        {t === "critical"
          ? <WarningCircle size={16} weight="fill" />
          : <ChartBar size={16} weight="regular" />}
      </div>

      <div className="flex-1 min-w-0">
        <div className={`text-[13px] font-semibold ${s.title_color} tracking-tight`}>
          {s.title}
        </div>
        <p className={`text-[12px] ${s.body_color} mt-0.5 leading-relaxed`}>
          {t === "critical" ? (
            <>
              Você usou <b>{used.toLocaleString("pt-BR")}</b> de {grant.toLocaleString("pt-BR")} créditos.
              {wallet.enforcement_enabled ? (
                <> Mensagens, IA e campanhas estão pausadas até você comprar um pacote ou aguardar a renovação do plano.</>
              ) : (
                <> O plano renova automaticamente no próximo ciclo; se quiser não esperar, compre um pacote extra.</>
              )}
            </>
          ) : (
            <>
              Você já usou <b>{percent}%</b> dos seus créditos do mês ({used.toLocaleString("pt-BR")} de {grant.toLocaleString("pt-BR")}).
              Considere comprar um pacote extra pra evitar parar a operação.
            </>
          )}
        </p>

        {/* mini progress bar */}
        <div className="mt-2 h-1 rounded-full bg-white/60 overflow-hidden">
          <div
            className={`h-full transition-all ${
              t === "critical" ? "bg-rose-600"
              : t === "warn90" ? "bg-orange-500"
              : "bg-amber-500"
            }`}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      <button
        onClick={() => navigate("/app/financeiro")}
        data-testid={`credit-banner-${t}-cta`}
        className={`flex-shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-medium transition-colors ${
          t === "critical"
            ? "bg-rose-600 text-white hover:bg-rose-700"
            : t === "warn90"
            ? "bg-orange-600 text-white hover:bg-orange-700"
            : "bg-amber-700 text-white hover:bg-amber-800"
        }`}
      >
        {s.cta}
        <ArrowRight size={11} weight="bold" />
      </button>
    </div>
  );
}
